import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Tooltip } from 'antd';
import { 
    UserOutlined, 
    LogoutOutlined, 
    MailOutlined,
    SettingOutlined,
    LoginOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import '../../styles/layout/SideMenuLayout.css';

import { useAuth } from '../../hooks/useAuth';

const SideMenuLayout = ({ children }) => {
    const { isLoggedIn, logout, isAdmin } = useAuth();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const [isHovered, setIsHovered] = useState(false);
    const [position, setPosition] = useState(() => {
        return localStorage.getItem('sideMenuPosition') || 'right';
    });


    // Save position to localStorage
    useEffect(() => {
        localStorage.setItem('sideMenuPosition', position);
    }, [position]);

    // Language toggle functionality
    const toggleLanguage = () => {
        const newLanguage = i18n.language === 'fr' ? 'en' : 'fr';
        i18n.changeLanguage(newLanguage);
    };

    const getNextLanguageFlag = () => {
        return i18n.language === 'fr' ? '🇺🇸' : '🇫🇷';
    };

    const getLanguageTooltip = () => {
        return i18n.language === 'fr' ? 'Switch to English' : 'Passer en Français';
    };

    const isActive = (path) => {
        if (location.pathname === path) return true;
        if (path.startsWith('/settings') && location.pathname.startsWith('/settings')) return true;
        return false;
    };

    // Cycle through positions: right -> bottom -> left -> top -> right
    const cyclePosition = () => {
        const positions = ['right', 'bottom', 'left', 'top'];
        const currentIndex = positions.indexOf(position);
        const nextIndex = (currentIndex + 1) % positions.length;
        setPosition(positions[nextIndex]);
    };

    const profileMenuItems = [
        { key: 'mailbox', icon: <MailOutlined />, label: t('layout.navigation.mailbox'), path: '/profile/mailbox' },
        { key: 'settings', icon: <SettingOutlined />, label: t('layout.navigation.settings'), path: '/settings#profile' },
    ];

    // Unauthenticated menu items
    const guestMenuItems = [
        { 
            key: 'login', 
            icon: <LoginOutlined />, 
            label: t('layout.navigation.login'), 
            path: '/login' 
        },
        { 
            key: 'register', 
            icon: <UserOutlined />, 
            label: t('layout.navigation.signUp'), 
            path: '/register' 
        },
    ];

    const getAllMenuItems = () => {
        if (!isLoggedIn) return guestMenuItems;
        return profileMenuItems;
    };

    const menuItems = getAllMenuItems();

    return (
        <AntLayout className="side-menu-layout-container">
            
            {/* Futuristic Floating Sidebar */}
            <div 
                className={`floating-sidebar ${position} ${isHovered ? 'hovered' : ''}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Central Hub with 8-dot pattern */}
                <div className="sidebar-hub" onClick={cyclePosition}>
                    <div className="hub-dots">
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                        <span className="hub-dot"></span>
                    </div>
                    <div className="hub-ring"></div>
                </div>

                {/* Menu Items Container */}
                <div className="sidebar-menu-items">
                    {/* Navigation Items */}
                    {menuItems.map((item) => (
                        <Tooltip 
                            key={item.key}
                            title={item.label}
                            placement={position === 'right' ? 'left' : position === 'left' ? 'right' : position === 'top' ? 'bottom' : 'top'}
                        >
                            <Link 
                                to={item.path}
                                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                            >
                                <span className="sidebar-item-icon">{item.icon}</span>
                            </Link>
                        </Tooltip>
                    ))}

                    {/* Divider before utilities */}
                    <div className="sidebar-divider"></div>

                    {/* Language Switcher */}
                    <Tooltip 
                        title={getLanguageTooltip()}
                        placement={position === 'right' ? 'left' : position === 'left' ? 'right' : position === 'top' ? 'bottom' : 'top'}
                    >
                        <button 
                            type="button"
                            onClick={toggleLanguage}
                            className="sidebar-item sidebar-language"
                        >
                            <span className="sidebar-item-icon sidebar-flag">{getNextLanguageFlag()}</span>
                        </button>
                    </Tooltip>

                    {/* Logout (if logged in) */}
                    {isLoggedIn && (
                        <Tooltip 
                            title={t('layout.navigation.signOut')}
                            placement={position === 'right' ? 'left' : position === 'left' ? 'right' : position === 'top' ? 'bottom' : 'top'}
                        >
                            <button 
                                type="button"
                                onClick={logout}
                                className="sidebar-item sidebar-logout"
                            >
                                <span className="sidebar-item-icon"><LogoutOutlined /></span>
                            </button>
                        </Tooltip>
                    )}
                </div>

                {/* Glow effect */}
                <div className="sidebar-glow"></div>
            </div>

            {/* Main Content Area */}
            <main className="side-menu-main-content">
                {children}
            </main>
        </AntLayout>
    );
};

export default SideMenuLayout;
